import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronRight, Folder, FolderSearch } from 'lucide-react';
import type { FolderDetail } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import DocTypeCombobox, { type DocTypeOption } from '../components/DocTypeCombobox';
import DocTypeLinkRow from '../components/DocTypeLinkRow';
import FileRow from '../components/FileRow';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';
import BackButton from '../components/BackButton';
import SourceLogo from '../components/SourceLogo';

export default function FolderDetailPage() {
    const { authFetch, isAdmin } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { sourceId } = useParams<{ sourceId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const path = searchParams.get('path') ?? '';
    // Types suggested via the querystring (from a Browse flow or added here); they lead the candidate list and carry into subfolders.
    const addedTypes = searchParams.getAll('type');
    const typeSuffix = addedTypes.map(t => `&type=${encodeURIComponent(t)}`).join('');

    const [folder, setFolder] = useState<FolderDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [allDocTypes, setAllDocTypes] = useState<DocTypeOption[]>([]);

    const folderUrl = `/connections/${sourceId}/folder?path=${encodeURIComponent(path)}`;
    const linkUrl = (definitionId: string) =>
        `/workspace/folder-links/${definitionId}?source_id=${encodeURIComponent(sourceId ?? '')}&path=${encodeURIComponent(path)}`;

    const reloadFolder = async () => {
        const res = await authFetch(folderUrl);
        if (res.ok) setFolder(await res.json());
    };

    useEffect(() => {
        const load = async () => {
            if (!sourceId) return;
            setLoading(true);
            setError(null);
            try {
                const res = await authFetch(folderUrl);
                if (!res.ok) throw new Error('Failed to load folder');
                setFolder(await res.json());
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load folder');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceId, path]);

    useEffect(() => {
        let stale = false;
        authFetch('/workspace/types')
            .then(r => r.ok ? r.json() : [])
            .then((data: DocTypeOption[]) => { if (!stale) setAllDocTypes(data); })
            .catch(() => { if (!stale) setAllDocTypes([]); });
        return () => { stale = true; };
    }, []);

    // Adds a suggestion to the querystring candidates; the person then links it from its row.
    const addSuggestion = async (definitionId: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (!next.getAll('type').includes(definitionId)) next.append('type', definitionId);
            return next;
        });
    };

    const handleLinkType = async (definitionId: string) => {
        const res = await authFetch(linkUrl(definitionId), { method: 'POST' });
        if (!res.ok) {
            showToast('Failed to link folder', 'error');
            return;
        }
        await reloadFolder();
    };

    const handleUnlinkType = async (definitionId: string) => {
        if (!window.confirm('Unlink this folder from the document type? Files already linked stay linked.')) return;
        const res = await authFetch(linkUrl(definitionId), { method: 'DELETE' });
        if (!res.ok) {
            showToast('Failed to unlink folder', 'error');
            return;
        }
        await reloadFolder();
    };

    if (loading) return <PageLoader label="Loading folder..." />;
    if (error || !folder) return <PageError title="Failed to load folder" message={error || 'Folder not found'} />;

    const linkedTo = folder.linked_to ?? [];
    const candidateFor = folder.candidate_for ?? [];
    const candidateForShown = (() => {
        const extras = [];
        const seen = new Set<string>();
        for (const typeId of addedTypes) {
            if (seen.has(typeId)) continue;
            seen.add(typeId);
            if (linkedTo.some(l => l.definition_id === typeId)) continue;
            if (candidateFor.some(c => c.definition_id === typeId)) continue;
            const dt = allDocTypes.find(t => t.id === typeId);
            if (!dt) continue;
            extras.push({
                definition_id: dt.id,
                document_type_name: dt.name,
                section_id: dt.section_id,
                section_name: dt.section_name,
                score: 0,
            });
        }
        return [...extras, ...candidateFor];
    })();
    const segments = folder.path.split('/').filter(Boolean);
    const goToFolder = (segs: string[]) =>
        navigate(`/connections/${sourceId}/folder?path=${encodeURIComponent(segs.join('/'))}${typeSuffix}`);

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                    <BackButton className="mt-3" />
                    <div className="navaris-tile-tint w-12 h-12 rounded-xl flex border border-softgrey items-center justify-center shrink-0">
                        <Folder className="w-6 h-6 text-brand" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-graphite wrap-break-word">{folder.name}</h3>
                        <div className="flex items-center flex-wrap gap-1.5 mt-1 text-sm text-coolslate min-w-0">
                            <Link
                                to={`/connections/${sourceId}`}
                                className="inline-flex items-center gap-1.5 hover:text-brand-dark transition-colors"
                            >
                                {folder.source_type && (
                                    <SourceLogo type={folder.source_type} size="sm" />
                                )}
                                {folder.source}
                            </Link>
                            {segments.map((segment, idx) => {
                                const isLast = idx === segments.length - 1;
                                return (
                                    <span key={idx} className="inline-flex items-center gap-1.5">
                                        <ChevronRight className="w-3.5 h-3.5 text-coolslate/60" />
                                        {isLast ? (
                                            <span className="font-medium text-graphite">{segment}</span>
                                        ) : (
                                            <button
                                                onClick={() => goToFolder(segments.slice(0, idx + 1))}
                                                className="hover:text-brand-dark transition-colors"
                                            >
                                                {segment}
                                            </button>
                                        )}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Linking a folder auto-links the files directly inside it, now and on each future sync. */}
            <div className="mb-4">
                <h4 className="navaris-h3">Document types</h4>
                <p className="text-[13px] text-coolslate mt-1">
                    Link this folder to a document type to auto-link the files inside it now and on each future sync.
                </p>
            </div>
            <div className="mb-6 space-y-3">
                <DocTypeCombobox
                    mode="combobox"
                    allTypes={allDocTypes}
                    excludeDefinitions={new Set([
                        ...linkedTo.map(l => l.definition_id),
                        ...candidateForShown.map(c => c.definition_id),
                    ])}
                    onLink={addSuggestion}
                    immediate
                    placeholder="Suggest a document type..."
                    canCreate={isAdmin}
                />
            </div>
            {(linkedTo.length > 0 || candidateForShown.length > 0) && (
                <div className="mb-8 space-y-6">
                    {linkedTo.length > 0 && (
                        <div className="space-y-1.5">
                            {linkedTo.map(l => (
                                <DocTypeLinkRow
                                    key={`${l.section_id}:${l.definition_id}`}
                                    definitionId={l.definition_id}
                                    name={l.document_type_name}
                                    sectionName={l.section_name}
                                    kind="linked"
                                    onAction={() => handleUnlinkType(l.definition_id)}
                                />
                            ))}
                        </div>
                    )}
                    {candidateForShown.length > 0 && (
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {candidateForShown.map(c => (
                                <DocTypeLinkRow
                                    key={c.definition_id}
                                    definitionId={c.definition_id}
                                    name={c.document_type_name}
                                    sectionName={c.section_name}
                                    kind="candidate"
                                    onAction={() => handleLinkType(c.definition_id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="mb-4 flex items-center justify-between gap-4">
                <h4 className="navaris-h3">Files in this folder</h4>
                <Link
                    to={`/connections/${sourceId}?folder=${encodeURIComponent(folder.path)}${typeSuffix}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-brand bg-brand/8 hover:bg-brand/15 border border-brand/20 rounded-md transition-colors cursor-pointer shrink-0"
                    title="Browse this folder in the source"
                >
                    <FolderSearch className="w-3 h-3" />
                    Browse
                </Link>
            </div>
            <div className="rounded-lg border border-softgrey bg-white p-2">
                {folder.folders.length === 0 && folder.files.length === 0 ? (
                    <p className="text-center text-coolslate py-6 text-sm">This folder is empty</p>
                ) : (
                    <div className="space-y-0.5">
                        {folder.folders.length > 0 && (
                            <div className="flex items-center gap-2 px-3 py-2 text-xs text-coolslate">
                                <Folder className="w-3.5 h-3.5" />
                                {folder.folders.length} subfolder{folder.folders.length !== 1 ? 's' : ''}
                            </div>
                        )}
                        {folder.files.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-coolslate">No files directly in this folder</p>
                        ) : folder.files.map(file => (
                            <FileRow key={file.id} file={file} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
